// Inventory contracts (docs/use-cases/inventory.md, UC-INVENTORY-001..013; modules-and-api.md §5.12; decisions D10, D14 and the
// working decisions W4, W5, W9, W10 and W11 of docs/sprints/sprint-04.md). Not to be confused with `openapi/inventory.ts`, the
// route inventory.
//
// Sprint 4 foundation: the vocabularies the inventory tables (their CHECK constraints) and routes share, spelled as the use
// cases spell them, with the screens' words where the use cases give them. The status machines of serials, stock takes,
// transfers and adjustments — and their status enums — are in status-machines.ts; whole-unit quantities, unit costs and the
// serial-number format in primitives.ts; the arithmetic in `@visionx/money` (`weightedAverageCost`, `timesQuantity`,
// `backOutCost`). The request and response schemas are the inventory module's, added with its routes (Sprint 4 stage 4) at the
// end of this file.
import { z } from "zod";
import { PRODUCT_CATEGORIES, type ProductCategory } from "./catalogue";
import { PRICE_SOURCES } from "./pricing";
import { LINE_QUANTITY_MAX, SUPPLIER_SERIAL_MAX_LENGTH, UNIT_COST_MAX_CENTS } from "./primitives";
import { SERIAL_STATUSES, STOCK_TAKE_STATUSES, STOCK_TRANSFER_STATUSES, type SerialStatus, type StockTakeStatus, type StockTransferStatus } from "./status-machines";

/**
 * BR-INVENTORY-002: a unit on hand is in exactly one of five states; on hand is their sum and available is the in-store count.
 * A unit sent on a transfer stays in the sender's on hand, In transit, until it is received (W10).
 */
export const STOCK_STATES = ["InStore", "Reserved", "AtLab", "Damaged", "InTransit"] as const;
export type StockState = (typeof STOCK_STATES)[number];
export const STOCK_STATE_LABELS: Readonly<Record<StockState, string>> = {
  InStore: "In store", Reserved: "Reserved", AtLab: "At lab", Damaged: "Damaged", InTransit: "In transit",
};

/** BR-INVENTORY-005: the outside parties a movement comes from or goes to — none of them counts toward on hand. */
export const STOCK_OUTSIDE_PARTIES = ["Supplier", "Customer", "Scrap", "Opening", "Adjust", "OtherBranch"] as const;
export type StockOutsideParty = (typeof STOCK_OUTSIDE_PARTIES)[number];
/** A movement's `from` and `to`: one of the five states or an outside party (BR-INVENTORY-004). */
export const STOCK_MOVEMENT_ENDPOINTS = [...STOCK_STATES, ...STOCK_OUTSIDE_PARTIES] as const;
export type StockMovementEndpoint = (typeof STOCK_MOVEMENT_ENDPOINTS)[number];

/**
 * BR-INVENTORY-005: the movement types, with the screens' words. Sprint 4 writes the first seven; the rest go through the same
 * ledger from the sprint `STOCK_MOVEMENT_TYPE_SPRINT` names.
 */
export const STOCK_MOVEMENT_TYPES = [
  "OpeningBalance", "Receipt", "Adjustment", "WriteOff", "StockTake", "TransferOut", "TransferIn",
  "Reserve", "Release", "Sale", "ToLab", "FromLab", "Return", "CostRevaluation",
] as const;
export type StockMovementType = (typeof STOCK_MOVEMENT_TYPES)[number];
export const STOCK_MOVEMENT_TYPE_LABELS: Readonly<Record<StockMovementType, string>> = {
  OpeningBalance: "Opening stock", Receipt: "Received", Adjustment: "Adjusted", WriteOff: "Written off", StockTake: "Counted",
  TransferOut: "Transferred out", TransferIn: "Transferred in",
  Reserve: "Reserved", Release: "Released", Sale: "Sold", ToLab: "Sent to lab", FromLab: "Back from lab", Return: "Returned",
  CostRevaluation: "Cost revaluation",
};
/**
 * BR-INVENTORY-005: a movement in the screens' words. A sent transfer's units cancelled back into the sender's store (in transit →
 * in store) keep the type `TransferOut`, the Send they reverse, but read as what they are — "Transfer cancelled", never
 * "Transferred out" (UC-INVENTORY-011 A4, A5; TC-INVENTORY-160).
 */
export function stockMovementLabel(m: { type: StockMovementType; fromState: StockMovementEndpoint; toState: StockMovementEndpoint }): string {
  if (m.type === "TransferOut" && m.fromState === "InTransit" && m.toState === "InStore") return "Transfer cancelled";
  return STOCK_MOVEMENT_TYPE_LABELS[m.type];
}
/** BR-INVENTORY-005: the sprint that first writes each movement type. */
export const STOCK_MOVEMENT_TYPE_SPRINT: Readonly<Record<StockMovementType, number>> = {
  OpeningBalance: 4, Receipt: 4, Adjustment: 4, WriteOff: 4, StockTake: 4, TransferOut: 4, TransferIn: 4,
  Reserve: 5, Release: 5, Sale: 6, Return: 6, ToLab: 7, FromLab: 7, CostRevaluation: 7,
};

/** BR-INVENTORY-011: the serial panel's and the transfer line's words for a serial's status (an Available unit is In store). */
export const SERIAL_STATUS_LABELS: Readonly<Record<SerialStatus, string>> = {
  Available: "Available", Reserved: "Reserved", AtLab: "At lab", Sold: "Sold", InTransit: "In transit", Damaged: "Damaged",
  WrittenOff: "Written off", Returned: "Returned",
};
/**
 * BR-INVENTORY-009, BR-PURCHASING-016: where a serial's number came from — the tenant sequencer (`YYYY-MM-DD-NNN`), or the
 * supplier's own serial or EAN kept instead, which takes no number.
 */
export const SERIAL_SOURCES = ["Generated", "SupplierSerial"] as const;
export type SerialSource = (typeof SERIAL_SOURCES)[number];

/**
 * BR-INVENTORY-013: the reason an adjustment names, chosen by a person — Damaged, Lost or theft, Correction, Display or sample,
 * Returned to supplier or Other. `StockTakeVariance` is never chosen by hand (UC-INVENTORY-006 E3): only a finalised count
 * writes it, on its movements (`STOCK_MOVEMENT_REASONS`).
 */
export const ADJUSTMENT_REASONS = ["Damaged", "LostTheft", "Correction", "DisplaySample", "ReturnedToSupplier", "Other"] as const;
export type AdjustmentReason = (typeof ADJUSTMENT_REASONS)[number];
/** BR-INVENTORY-004, BR-INVENTORY-019: the reason a movement or a written-off serial carries — an adjustment's, or a count's variance. */
export const STOCK_MOVEMENT_REASONS = [...ADJUSTMENT_REASONS, "StockTakeVariance"] as const;
export type StockMovementReason = (typeof STOCK_MOVEMENT_REASONS)[number];
export const STOCK_MOVEMENT_REASON_LABELS: Readonly<Record<StockMovementReason, string>> = {
  Damaged: "Damaged", LostTheft: "Lost or theft", Correction: "Correction", DisplaySample: "Display or sample",
  ReturnedToSupplier: "Returned to supplier", Other: "Other", StockTakeVariance: "Stock-take variance",
};
/**
 * BR-INVENTORY-013: what an adjustment line says became of its units where its reason leaves a choice — written off (to scrap),
 * quarantined (Damaged only; the units stay on hand as Damaged) or released from quarantine (Proposed, UC-INVENTORY-006 A5);
 * a line returned to the supplier or corrected takes its destination from its reason (TC-INVENTORY-027, -030, -031).
 */
export const ADJUSTMENT_LINE_OUTCOMES = ["WriteOff", "Quarantine", "Release"] as const;
export type AdjustmentLineOutcome = (typeof ADJUSTMENT_LINE_OUTCOMES)[number];

/**
 * BR-INVENTORY-016: a count is a full count, a cycle count scoped by category, brand or supplier, or the branch's opening stock
 * take (BR-INVENTORY-020, D10).
 */
export const STOCK_TAKE_KINDS = ["Full", "Cycle", "Opening"] as const;
export type StockTakeKind = (typeof STOCK_TAKE_KINDS)[number];
/** UC-INVENTORY-007 (open questions): the screens' words for a count's status — `Open` is shown In progress. */
export const STOCK_TAKE_STATUS_LABELS: Readonly<Record<StockTakeStatus, string>> = {
  Open: "In progress", Posting: "Posting…", Finalised: "Finalised", Abandoned: "Abandoned",
};
/**
 * BR-INVENTORY-018, BR-INVENTORY-019: what a counted line comes to. A missing serial is an expected serial nobody scanned, a
 * found unit one counted but not expected; units at a lab or in transit are confirmed, never counted (TC-INVENTORY-049,
 * -052, -053). `QtyVariance`, a quantity line counted other than expected, is the design's word (schema-catalogue.sql).
 */
export const STOCK_TAKE_LINE_RESULTS = ["Matched", "Missing", "Found", "QtyVariance", "AtLabConfirmed", "InTransitConfirmed"] as const;
export type StockTakeLineResult = (typeof STOCK_TAKE_LINE_RESULTS)[number];
/** Where a line an ended posting did not post sets its shelf price again by hand (UC-INVENTORY-010 A8 step 3; BR-PRICING-016). */
export const SHELF_PRICE_TARGETS = ["Serial", "Product", "Branch"] as const;
export type ShelfPriceTarget = (typeof SHELF_PRICE_TARGETS)[number];

/** UC-INVENTORY-011 (open questions): the screens' words for a transfer's status — a Sent transfer is shown In transit. */
export const STOCK_TRANSFER_STATUS_LABELS: Readonly<Record<StockTransferStatus, string>> = {
  Draft: "Draft", Sent: "In transit", PartiallyReceived: "Partially received", Received: "Received", Cancelled: "Cancelled",
};
/** §5.12: the transfers list's `?direction` — the acting branch's outbound transfers or its inbound ones. */
export const STOCK_TRANSFER_DIRECTIONS = ["outbound", "inbound"] as const;
export type StockTransferDirection = (typeof STOCK_TRANSFER_DIRECTIONS)[number];

/** The limits the inventory's use cases state (BR-INVENTORY-014, BR-INVENTORY-022, BR-INVENTORY-027). */
export const INVENTORY_LIMITS = {
  /** A re-order level: a whole number 0–9 999; 0 means not monitored. */
  reorderLevelMax: 9_999,
  /** An adjustment or a transfer holds at most 200 lines. */
  documentLinesMax: 200,
  /** A note or a reason, at most 500 characters; a serial's tag 40 and its notes 1 000. */
  noteMax: 500, serialTagMax: 40, serialNotesMax: 1_000,
  /**
   * An opening count posts at most this many lines and supplier serials kept together, and its post's typed costs and shelf prices
   * hold at most as many each (BR-INVENTORY-027, UC-INVENTORY-010 E14; Proposed, the third review of DVN's answers, 25 Oct 2026):
   * its post is checked in one request, whose reads of each line's product and cost stay within D1's queries an invocation.
   */
  openingLinesMax: 20_000,
} as const;

// ===========================================================================================================================
// The inventory module's request and response schemas (UC-INVENTORY-001..013; modules-and-api.md §5.12)
// ===========================================================================================================================
// A body field answers the codes the use cases name — `required`, `too_long`, `invalid`, `out_of_range`, `not_whole`, `zero`,
// `duplicate`, `too_many`, `not_allowed` — through the small field builders below, as the catalogue's contracts do. Money is
// whole cents (costs ex VAT, a serial's own price VAT-inclusive, W1); quantities are whole units (W4).

/** What a stock read searches and pages by (UC-INVENTORY-001 E2): a search of at most 100 characters, 1–200 rows a page. */
export const INVENTORY_QUERY = { qMax: 100, limitDefault: 50, limitMax: 200, deviceMax: 64, stockTakeNameMax: 80 } as const;

/** A stock line's words for the units it holds apart from the store (UC-INVENTORY-001 step 2's sub-line). */
export const STOCK_HOLD_KINDS = ["Reserved", "AtLab", "Transfer"] as const;
export type StockHoldKind = (typeof STOCK_HOLD_KINDS)[number];

/** Where a serial's unit is: one of the five states while it is on hand, `None` once it has left (Sold, Written off, Returned). */
export const SERIAL_LOCATIONS = ["InStore", "Reserved", "AtLab", "Damaged", "InTransit", "None"] as const;
export type SerialLocation = (typeof SERIAL_LOCATIONS)[number];
/** BR-INVENTORY-002, -011: the state a serial's unit is in, by its status. */
export const SERIAL_LOCATION_OF: Readonly<Record<SerialStatus, SerialLocation>> = {
  Available: "InStore", Reserved: "Reserved", AtLab: "AtLab", Sold: "None", InTransit: "InTransit", Damaged: "Damaged", WrittenOff: "None", Returned: "None",
};
/** BR-INVENTORY-014: the statuses whose unit is on hand, and so the only ones a serial may be edited in. */
export const SERIAL_ON_HAND_STATUSES: readonly SerialStatus[] = ["Available", "Reserved", "AtLab", "InTransit", "Damaged"];

/**
 * BR-INVENTORY-014: the fields of a serial an edit may not name — every one but its tag, notes and own selling price — refused
 * `not_editable` (UC-INVENTORY-004 E3) before anything else is checked.
 */
export const SERIAL_READ_ONLY_FIELDS = [
  "id", "serialNumber", "source", "serialSource", "productId", "branchId", "status", "location", "statusReason", "unitCostCents", "averageCostCents",
  "supplierId", "supplier", "receivedOn", "receivedAt", "goodsReceiptId", "reservedOrderId", "labOrderId", "soldInvoiceId", "barcodeValue", "voidedAt",
] as const;

/** What an adjustment line's units become — `WriteOff`, `Quarantine` and `Release` are chosen; the rest follow from the reason and sign. */
export const ADJUSTMENT_LINE_RESULTS = ["Increase", "WriteOff", "Quarantine", "Release", "ReturnToSupplier", "Correction"] as const;
export type AdjustmentLineResult = (typeof ADJUSTMENT_LINE_RESULTS)[number];
/** An adjustment line's units leave from — in store, or damaged (a quarantined unit released or written off) — or arrive from an increase. */
export const ADJUSTMENT_LINE_FROM_STATES = ["InStore", "Damaged", "Adjust"] as const;
/** Scrap (written off), Damaged (quarantined), Supplier (returned), Adjust (corrected), InStore (an increase or a release). */
export const ADJUSTMENT_LINE_TO_STATES = ["InStore", "Damaged", "Scrap", "Supplier", "Adjust"] as const;
/** BR-INVENTORY-016: units counted physically (in store, reserved, damaged) — or at a lab or in transit, confirmed, not counted. */
export const STOCK_TAKE_EXPECTED_STATES = ["InStore", "AtLab", "InTransit"] as const;
export type StockTakeExpectedState = (typeof STOCK_TAKE_EXPECTED_STATES)[number];
/** UC-INVENTORY-008: how a scanned code resolved — a serial first, then a product barcode, then an SKU (BR-INVENTORY-010). */
export const COUNT_MATCHES = ["serial", "barcode", "sku", "product"] as const;

type FieldCtx = z.RefinementCtx;
const failField = (ctx: FieldCtx, code: string, message: string, path?: (string | number)[]): void => {
  ctx.addIssue({ code: "custom", message, params: { code }, ...(path ? { path } : {}) });
};
const isAbsent = (v: unknown): boolean => v === undefined || v === null;
const isBlank = (v: unknown): boolean => isAbsent(v) || (typeof v === "string" && v.trim() === "");

/** Text checked to a length: absent, null or blank is `required` when required; trimmed. */
function checkLength(v: unknown, max: number, ctx: FieldCtx, required: boolean): void {
  if (isBlank(v)) { if (required) failField(ctx, "required", "required"); return; }
  if (typeof v !== "string") return failField(ctx, "invalid", "must be text");
  if (v.trim().length > max) failField(ctx, "too_long", `at most ${max} characters`);
}
const requiredString = (max: number) => z.unknown().superRefine((v, ctx) => checkLength(v, max, ctx, true)).transform((v) => (v as string).trim());
/** Text a body may leave out; `null` or blank is none. */
const optionalString = (max: number) => z.unknown().superRefine((v, ctx) => checkLength(v, max, ctx, false))
  .transform((v): string | null => (isBlank(v) ? null : (v as string).trim())).optional();

/** A whole number within a range: a fraction is `not_whole`, a value outside `out_of_range`; `zero` refused when asked. */
function checkWhole(v: unknown, o: { min: number; max: number; nonZero?: boolean }, ctx: FieldCtx, required: boolean): void {
  if (isAbsent(v)) { if (required) failField(ctx, "required", "required"); return; }
  if (typeof v !== "number" || !Number.isFinite(v)) return failField(ctx, "invalid", "a number");
  if (!Number.isInteger(v)) return failField(ctx, "not_whole", "a whole number");
  if (o.nonZero && v === 0) return failField(ctx, "zero", "a change of no units");
  if (v < o.min || v > o.max) failField(ctx, "out_of_range", `from ${o.min} to ${o.max}`);
}
const requiredWhole = (o: { min: number; max: number; nonZero?: boolean }) => z.unknown().superRefine((v, ctx) => checkWhole(v, o, ctx, true)).transform((v) => v as number);
const optionalWhole = (o: { min: number; max: number }) => z.unknown().superRefine((v, ctx) => checkWhole(v, o, ctx, false)).transform((v) => (isAbsent(v) ? null : (v as number))).optional();

const idOf = (required: boolean) => z.unknown().superRefine((v, ctx) => {
  if (isAbsent(v)) { if (required) failField(ctx, "required", "required"); return; }
  if (typeof v !== "string" || v.trim().length === 0 || v.length > 64) failField(ctx, "invalid", "an id");
});
const requiredIdField = () => idOf(true).transform((v) => (v as string).trim());
const optionalIdField = () => idOf(false).transform((v) => (isAbsent(v) ? null : (v as string).trim())).optional();

const enumField = (values: readonly string[], required: boolean) => z.unknown().superRefine((v, ctx) => {
  if (isAbsent(v)) { if (required) failField(ctx, "required", "required"); return; }
  if (typeof v !== "string" || !values.includes(v)) failField(ctx, "invalid", `one of ${values.join(", ")}`);
});

/** A local calendar date `YYYY-MM-DD` that exists (2026-02-30 does not). */
export function isCalendarDay(s: unknown): s is string {
  if (typeof s !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(s)) return false;
  const d = new Date(`${s}T00:00:00Z`);
  return !Number.isNaN(d.getTime()) && d.toISOString().slice(0, 10) === s;
}
const dayField = (required: boolean) => z.unknown().superRefine((v, ctx) => {
  if (isAbsent(v)) { if (required) failField(ctx, "required", "required"); return; }
  if (!isCalendarDay(v)) failField(ctx, "invalid", "a calendar date YYYY-MM-DD");
});

// --- Query parameters ------------------------------------------------------------------------------------------------------

const limitQuery = z.unknown().superRefine((v, ctx) => {
  const n = typeof v === "string" && /^\d+$/.test(v) ? Number(v) : NaN;
  if (!Number.isInteger(n) || n < 1 || n > INVENTORY_QUERY.limitMax) failField(ctx, "out_of_range", `1 to ${INVENTORY_QUERY.limitMax}`);
}).transform((v): number => Number(v)).default(INVENTORY_QUERY.limitDefault);
const cursorQuery = z.unknown().superRefine((v, ctx) => { if (typeof v !== "string" || v.length === 0 || v.length > 512) failField(ctx, "invalid", "a cursor this list issued"); })
  .transform((v) => v as string).optional();
const flagQuery = z.enum(["true", "false"]).optional();
/** `?branchId` — a branch of the organisation, or `all` for an Owner or Admin (BR-INVENTORY-024). */
const branchQuery = z.string().trim().min(1).max(32).optional();
const queryText = (max: number) => z.unknown().superRefine((v, ctx) => {
  if (typeof v !== "string") return failField(ctx, "invalid", "text");
  if (v.trim().length > max) failField(ctx, "too_long", `at most ${max} characters`);
}).transform((v): string | undefined => ((v as string).trim() === "" ? undefined : (v as string).trim())).optional();
const enumQuery = <T extends string>(values: readonly T[]) => z.unknown().superRefine((v, ctx) => {
  if (typeof v !== "string" || !(values as readonly string[]).includes(v)) failField(ctx, "invalid", `one of ${values.join(", ")}`);
}).transform((v) => v as T).optional();
/** `?category=Frame,ContactLens` — one category or several, comma-separated. */
const categoriesQuery = z.unknown().superRefine((v, ctx) => {
  const parts = typeof v === "string" ? v.split(",").map((s) => s.trim()).filter(Boolean) : [];
  if (parts.length === 0 || parts.some((p) => !(PRODUCT_CATEGORIES as readonly string[]).includes(p))) failField(ctx, "invalid", `one or more of ${PRODUCT_CATEGORIES.join(", ")}`);
}).transform((v) => [...new Set((v as string).split(",").map((s) => s.trim()).filter(Boolean))] as ProductCategory[]).optional();
const dayQuery = z.unknown().superRefine((v, ctx) => { if (!isCalendarDay(v)) failField(ctx, "invalid", "a calendar date YYYY-MM-DD"); }).transform((v) => v as string).optional();
const idQuery = z.string().trim().min(1).max(64).optional();

/** A user a stock record names: who moved, counted, posted or authorised. */
export const InventoryUserRefSchema = z.object({ id: z.string(), controlId: z.number().int().nullable(), displayName: z.string() });
export type InventoryUserRef = z.infer<typeof InventoryUserRefSchema>;
/** A document a movement or a hold names: its type, id and number (BR-INVENTORY-004). */
export const StockDocumentRefSchema = z.object({ type: z.string(), id: z.string().nullable(), number: z.string().nullable() });
export type StockDocumentRef = z.infer<typeof StockDocumentRefSchema>;

// --- Stock lines, the summary, availability and re-order levels (UC-INVENTORY-001, -002) -----------------------------------

export const StockListQuerySchema = z.object({
  branchId: branchQuery,
  state: enumQuery(STOCK_STATES),
  belowReOrder: flagQuery,
  category: categoriesQuery,
  q: queryText(INVENTORY_QUERY.qMax),
  limit: limitQuery,
  cursor: cursorQuery,
  count: flagQuery,
});
export type StockListQuery = z.infer<typeof StockListQuerySchema>;

export const StockHoldSchema = z.object({
  kind: z.enum(STOCK_HOLD_KINDS),
  units: z.number().int(),
  /** The order, lab order or transfer holding the units — its number as the fixture prints it. */
  document: StockDocumentRefSchema.nullable(),
  /** A transfer's status (Draft, Sent …). */
  status: z.string().nullable(),
  serialNumber: z.string().nullable(),
});
export type StockHold = z.infer<typeof StockHoldSchema>;

export const StockLineViewSchema = z.object({
  /** The stock line's id; null for a product the branch has no line for. */
  id: z.string().nullable(),
  branchId: z.string(), branchCode: z.string(), branchName: z.string(),
  productId: z.string(), sku: z.string(), name: z.string(), category: z.enum(PRODUCT_CATEGORIES), brand: z.string().nullable(),
  /** A deactivated product still holding stock keeps its line (BR-INVENTORY-001). */
  inactive: z.boolean(),
  trackSerials: z.boolean(),
  onHand: z.number().int(), inStore: z.number().int(), reserved: z.number().int(), atLab: z.number().int(), damaged: z.number().int(), inTransit: z.number().int(),
  /** In store (BR-INVENTORY-002). */
  available: z.number().int(),
  reOrderLevel: z.number().int(),
  /** BR-INVENTORY-022: a level above 0 with available at or below it. */
  belowReOrder: z.boolean(),
  averageCostCents: z.number().int(),
  /** On hand × the weighted average (BR-INVENTORY-008). */
  valueCents: z.number().int(),
  lastReceivedOn: z.string().nullable(),
  lastMovementAt: z.string().nullable(),
  lastCounted: z.object({ on: z.string(), stockTakeId: z.string(), stockTakeNumber: z.string(), varianceQty: z.number().int() }).nullable(),
  holds: z.array(StockHoldSchema),
  /** The serial a search by serial number found on this line (UC-INVENTORY-001 A2). */
  matchedSerial: z.string().nullable(),
});
export type StockLineView = z.infer<typeof StockLineViewSchema>;

export const StockTotalsSchema = z.object({
  lines: z.number().int(), onHand: z.number().int(), inStore: z.number().int(), reserved: z.number().int(), atLab: z.number().int(),
  damaged: z.number().int(), inTransit: z.number().int(), valueCents: z.number().int(),
});
export type StockTotals = z.infer<typeof StockTotalsSchema>;

/** A page of stock lines and the totals of the lines shown ("Lines shown 220 214 5 1 0 … R 43 186.00"). */
export const StockPageSchema = z.object({
  items: z.array(StockLineViewSchema),
  nextCursor: z.string().optional(),
  totalCount: z.number().int().optional(),
  totals: StockTotalsSchema,
});
export type StockPage = z.infer<typeof StockPageSchema>;

export const StockSummaryQuerySchema = z.object({ branchId: branchQuery });

const stateCounts = { onHand: z.number().int(), inStore: z.number().int(), reserved: z.number().int(), atLab: z.number().int(), damaged: z.number().int(), inTransit: z.number().int() };
const summaryFields = {
  branchId: z.string().nullable(), branchName: z.string().nullable(),
  lines: z.number().int(), valueCents: z.number().int(),
  ...stateCounts,
  frames: z.object(stateCounts),
  /** When the unit longest at a lab was sent (UC-INVENTORY-001 step 3, "oldest sent 29 Aug"). */
  oldestAtLabSentAt: z.string().nullable(),
  /** The lines below their re-order level (BR-INVENTORY-022): frames counted and named beside the other lines — Today's line (TC-INVENTORY-175). */
  belowReOrder: z.object({ frames: z.number().int(), other: z.number().int(), frameSkus: z.array(z.string()), frameNames: z.array(z.string()), otherSkus: z.array(z.string()), otherNames: z.array(z.string()) }),
};
export const StockBranchSummarySchema = z.object(summaryFields);
/** The stats strip (UC-INVENTORY-001 step 3); for All branches, each branch's beside their total (A1). */
export const StockSummarySchema = z.object({ ...summaryFields, branches: z.array(StockBranchSummarySchema) });
export type StockSummary = z.infer<typeof StockSummarySchema>;
export type StockBranchSummary = z.infer<typeof StockBranchSummarySchema>;

export const ProductStockParamsSchema = z.object({ productId: z.string().min(1).max(64) });
export const AvailabilityQuerySchema = z.object({ branchId: branchQuery });
export const AvailableSerialSchema = z.object({
  id: z.string(), serialNumber: z.string(), tag: z.string().nullable(), colour: z.string().nullable(), size: z.string().nullable(),
  /** Its own selling price when set (VAT-inclusive, W1). */
  salesPriceCents: z.number().int().nullable(),
});
/** UC-INVENTORY-001 A3: the available quantity and the product's Available serials at the branch. */
export const AvailabilitySchema = z.object({
  productId: z.string(), branchId: z.string(), available: z.number().int(), onHand: z.number().int(), serials: z.array(AvailableSerialSchema),
});
export type Availability = z.infer<typeof AvailabilitySchema>;

/** UC-INVENTORY-002: a whole number 0–9 999; 0 means not monitored. */
export const ReOrderLevelInputSchema = z.strictObject({ level: requiredWhole({ min: 0, max: INVENTORY_LIMITS.reorderLevelMax }) });
export type ReOrderLevelInput = z.infer<typeof ReOrderLevelInputSchema>;

// --- Movements (UC-INVENTORY-003) ------------------------------------------------------------------------------------------

export const MovementListQuerySchema = z.object({
  branchId: branchQuery,
  productId: idQuery,
  serialId: idQuery,
  /** A serial by its number (UC-INVENTORY-003 A1). */
  serial: queryText(SUPPLIER_SERIAL_MAX_LENGTH),
  type: enumQuery(STOCK_MOVEMENT_TYPES),
  documentId: idQuery,
  documentNumber: queryText(64),
  userId: idQuery,
  from: dayQuery,
  to: dayQuery,
  limit: limitQuery,
  cursor: cursorQuery,
}).superRefine((v, ctx) => {
  if (v.from && v.to && v.to < v.from) failField(ctx, "before_from", "the range ends before it starts", ["to"]);
});
export type MovementListQuery = z.infer<typeof MovementListQuerySchema>;

export const StockMovementViewSchema = z.object({
  id: z.string(), branchId: z.string(), branchName: z.string(),
  occurredAt: z.string(),
  type: z.enum(STOCK_MOVEMENT_TYPES), typeLabel: z.string(),
  /** The SKU and name the movement was written with, never the product's as renamed since (BR-INVENTORY-006). */
  productId: z.string(), sku: z.string(), productName: z.string(),
  serialId: z.string().nullable(), serialNumber: z.string().nullable(),
  fromState: z.enum(STOCK_MOVEMENT_ENDPOINTS), toState: z.enum(STOCK_MOVEMENT_ENDPOINTS),
  quantity: z.number().int(),
  /** The signed change to on hand; 0 between two states (BR-INVENTORY-004). */
  onHandDelta: z.number().int(),
  unitCostCents: z.number().int().nullable(), totalCostCents: z.number().int().nullable(), avgCostAfterCents: z.number().int().nullable(),
  document: StockDocumentRefSchema.nullable(),
  reason: z.enum(STOCK_MOVEMENT_REASONS).nullable(), note: z.string().nullable(),
  user: InventoryUserRefSchema.nullable(),
  /** Who authorised it with a Control PIN, and the Control Log row (UC-INVENTORY-003 step 2). */
  authorisedBy: InventoryUserRefSchema.nullable(),
  controlLogId: z.string().nullable(),
  reversalOfMovementId: z.string().nullable(),
});
export type StockMovementView = z.infer<typeof StockMovementViewSchema>;
export const StockMovementPageSchema = z.object({ items: z.array(StockMovementViewSchema), nextCursor: z.string().optional() });

// --- Serials (UC-INVENTORY-004, -005) --------------------------------------------------------------------------------------

export const SerialListQuerySchema = z.object({
  q: queryText(SUPPLIER_SERIAL_MAX_LENGTH),
  productId: idQuery,
  branchId: branchQuery,
  status: enumQuery(SERIAL_STATUSES),
  supplierId: idQuery,
  receivedFrom: dayQuery,
  receivedTo: dayQuery,
  tag: queryText(INVENTORY_LIMITS.serialTagMax),
  limit: limitQuery,
  cursor: cursorQuery,
  count: flagQuery,
}).superRefine((v, ctx) => {
  if (v.receivedFrom && v.receivedTo && v.receivedTo < v.receivedFrom) failField(ctx, "before_from", "the range ends before it starts", ["receivedTo"]);
});
export type SerialListQuery = z.infer<typeof SerialListQuerySchema>;

const serialSummaryFields = {
  id: z.string(), serialNumber: z.string(), source: z.enum(SERIAL_SOURCES),
  productId: z.string(), sku: z.string(), productName: z.string(),
  branchId: z.string(), branchName: z.string(),
  status: z.enum(SERIAL_STATUSES), statusLabel: z.string(), location: z.enum(SERIAL_LOCATIONS),
  statusChangedAt: z.string().nullable(), statusReason: z.enum(STOCK_MOVEMENT_REASONS).nullable(),
  receivedOn: z.string().nullable(),
  supplier: z.object({ id: z.string(), name: z.string() }).nullable(),
  goodsReceiptId: z.string().nullable(),
  /** Its receipt cost ex VAT, for reference only (W5). */
  unitCostCents: z.number().int().nullable(),
  /** Its own selling price, VAT-inclusive (W1); null when it sells at its branch's price. */
  salesPriceCents: z.number().int().nullable(),
  tag: z.string().nullable(),
  reservedOrderId: z.string().nullable(),
  labOrderId: z.string().nullable(), sentToLabAt: z.string().nullable(),
  /** Whole days since it was sent to the lab, in the organisation's calendar; null when not at a lab. */
  daysAtLab: z.number().int().nullable(),
  soldInvoiceId: z.string().nullable(), soldAt: z.string().nullable(),
};
export const SerialSummarySchema = z.object(serialSummaryFields);
export type SerialSummary = z.infer<typeof SerialSummarySchema>;
export const SerialPageSchema = z.object({
  items: z.array(SerialSummarySchema),
  nextCursor: z.string().optional(),
  totalCount: z.number().int().optional(),
  /** How many of the serials listed are on hand (UC-INVENTORY-004 A8: "6 serials, 3 on hand"). */
  onHandCount: z.number().int(),
});

export const SerialViewSchema = z.object({
  ...serialSummaryFields,
  notes: z.string().nullable(),
  /** The serial itself — the supplier's serial where one was kept (BR-INVENTORY-010). */
  barcodeValue: z.string(),
  /** The branch's weighted average for the product (W5). */
  averageCostCents: z.number().int().nullable(),
  /** The price the unit sells at now: its own, else the branch's, else the tenant's (UC-PRICING-008). */
  resolvedPrice: z.object({ priceCents: z.number().int().nullable(), source: z.enum(PRICE_SOURCES) }),
  priceOverriddenBy: InventoryUserRefSchema.nullable(), priceOverriddenAt: z.string().nullable(), priceOverrideReason: z.string().nullable(),
  /** What holds it: a draft or sent transfer, a reserving order, a lab order. */
  holds: z.array(z.object({ type: z.string(), id: z.string().nullable(), number: z.string().nullable(), status: z.string().nullable() })),
  /** A written-off serial's adjustment (or count) and its Control Log row (UC-INVENTORY-004 A6). */
  writtenOff: z.object({ document: StockDocumentRefSchema.nullable(), controlLogId: z.string().nullable(), at: z.string().nullable() }).nullable(),
  movements: z.array(StockMovementViewSchema),
  onHand: z.boolean(),
});
export type SerialView = z.infer<typeof SerialViewSchema>;

/**
 * BR-INVENTORY-014: a serial's tag (≤ 40), notes (≤ 1 000) and own selling price (whole cents R 0.00–R 999 999.99, `null` clears
 * it); a reason (≤ 500) goes with a price below the floor, as the Control Log row's — required whenever that price needs its Control
 * PIN, refused `422` (`reason`, `required`) before it is asked (UC-INVENTORY-004 E10; DVN's answer of 28 Oct 2026, change 11).
 */
export const SerialPatchInputSchema = z.strictObject({
  tag: optionalString(INVENTORY_LIMITS.serialTagMax),
  notes: optionalString(INVENTORY_LIMITS.serialNotesMax),
  salesPriceCents: optionalWhole({ min: 0, max: UNIT_COST_MAX_CENTS }),
  reason: optionalString(INVENTORY_LIMITS.noteMax),
});
export type SerialPatchInput = z.infer<typeof SerialPatchInputSchema>;

export const SerialVoidInputSchema = z.strictObject({ reason: requiredString(INVENTORY_LIMITS.noteMax) });
export type SerialVoidInput = z.infer<typeof SerialVoidInputSchema>;
export const SerialVoidResultSchema = z.object({
  id: z.string(), serialNumber: z.string(), voidedAt: z.string(), voidedBy: InventoryUserRefSchema.nullable(), reason: z.string(),
  controlLogId: z.string().nullable(), reversalMovementId: z.string(),
  /** The branch's average after the unit's cost was backed out (BR-INVENTORY-015). */
  averageCostCents: z.number().int(), onHand: z.number().int(),
});
export type SerialVoidResult = z.infer<typeof SerialVoidResultSchema>;

// --- Adjustments (UC-INVENTORY-006) ----------------------------------------------------------------------------------------

export const AdjustmentLineInputSchema = z.strictObject({
  productId: requiredIdField(),
  serialId: optionalIdField(),
  /** A non-zero whole change of at most 9 999 either way; a serial line changes by 1 (BR-INVENTORY-013). */
  quantityDelta: requiredWhole({ min: -LINE_QUANTITY_MAX, max: LINE_QUANTITY_MAX, nonZero: true }),
  outcome: enumField(ADJUSTMENT_LINE_OUTCOMES, false).transform((v) => (isAbsent(v) ? null : (v as AdjustmentLineOutcome))).optional(),
  /** An increase's unit cost ex VAT, whole cents R 0.00–R 999 999.99. */
  unitCostCents: optionalWhole({ min: 0, max: UNIT_COST_MAX_CENTS }),
});
export type AdjustmentLineInput = z.infer<typeof AdjustmentLineInputSchema>;

const adjustmentLines = z.unknown().superRefine((v, ctx) => {
  if (!Array.isArray(v) || v.length === 0) return failField(ctx, "required", "at least one line");
  if (v.length > INVENTORY_LIMITS.documentLinesMax) failField(ctx, "too_many", `at most ${INVENTORY_LIMITS.documentLinesMax} lines`);
}).pipe(z.array(AdjustmentLineInputSchema));

/** BR-INVENTORY-013, -027: one reason, a note, 1–200 lines. */
export const AdjustmentInputSchema = z.strictObject({
  reason: z.unknown().superRefine((v, ctx) => {
    if (isAbsent(v)) return failField(ctx, "required", "required");
    if (v === "StockTakeVariance") return failField(ctx, "not_allowed", "a stock-take variance is posted only by a finalised count");
    if (typeof v !== "string" || !(ADJUSTMENT_REASONS as readonly string[]).includes(v)) failField(ctx, "invalid", `one of ${ADJUSTMENT_REASONS.join(", ")}`);
  }).transform((v) => v as AdjustmentReason),
  note: optionalString(INVENTORY_LIMITS.noteMax),
  lines: adjustmentLines,
}).superRefine((v, ctx) => {
  if (!Array.isArray(v.lines)) return;
  const seen = new Set<string>();
  v.lines.forEach((l, i) => {
    if (l.serialId && Math.abs(l.quantityDelta) !== 1) failField(ctx, "serial_unit", "a serial line changes by 1", ["lines", i, "quantityDelta"]);
    if (l.serialId) {
      if (seen.has(l.serialId)) failField(ctx, "duplicate", "the same serial twice", ["lines", i, "serialId"]);
      seen.add(l.serialId);
    }
    if (l.quantityDelta > 0 && l.outcome !== "Release" && (l.unitCostCents === null || l.unitCostCents === undefined)) failField(ctx, "required", "an increase needs its unit cost ex VAT", ["lines", i, "unitCostCents"]);
    if (l.quantityDelta > 0 && (l.outcome === "WriteOff" || l.outcome === "Quarantine")) failField(ctx, "invalid", "a write-off or a quarantine lowers stock", ["lines", i, "outcome"]);
    if (l.quantityDelta < 0 && l.outcome === "Release") failField(ctx, "invalid", "a release returns a quarantined unit to the store", ["lines", i, "outcome"]);
    if (l.outcome === "Quarantine" && v.reason !== "Damaged") failField(ctx, "invalid", "only a Damaged unit is quarantined", ["lines", i, "outcome"]);
    if ((l.outcome === "Quarantine" || l.outcome === "Release") && !l.serialId) failField(ctx, "required", "a quarantine or a release names its serial", ["lines", i, "serialId"]);
  });
  // BR-INVENTORY-013 (Proposed): a note for Other and for every document with a decrease — the Control Log row's reason.
  const lowers = v.lines.some((l) => l.quantityDelta < 0);
  if ((v.reason === "Other" || lowers) && isBlank(v.note)) failField(ctx, "required", v.reason === "Other" ? "Other needs a note" : "a decrease needs a note", ["note"]);
});
export type AdjustmentInput = z.infer<typeof AdjustmentInputSchema>;

export const AdjustmentLineViewSchema = z.object({
  /** The SKU and name frozen when the adjustment posted (BR-INVENTORY-006). */
  id: z.string(), productId: z.string(), sku: z.string(), productName: z.string(),
  serialId: z.string().nullable(), serialNumber: z.string().nullable(),
  quantityDelta: z.number().int(),
  fromState: z.enum(ADJUSTMENT_LINE_FROM_STATES), toState: z.enum(ADJUSTMENT_LINE_TO_STATES),
  outcome: z.enum(ADJUSTMENT_LINE_RESULTS),
  unitCostCents: z.number().int().nullable(), valueCents: z.number().int().nullable(),
  movementId: z.string().nullable(),
});
export type AdjustmentLineView = z.infer<typeof AdjustmentLineViewSchema>;

const adjustmentHead = {
  id: z.string(), number: z.string(), branchId: z.string(), branchName: z.string(),
  status: z.enum(["Posted"]), reason: z.enum(ADJUSTMENT_REASONS), reasonLabel: z.string(),
  postedAt: z.string(), postedBy: InventoryUserRefSchema.nullable(), authorisedBy: InventoryUserRefSchema.nullable(), controlLogId: z.string().nullable(),
  /** The signed value at cost of every line. */
  totalValueCents: z.number().int(),
  lineCount: z.number().int(),
};
export const AdjustmentSummarySchema = z.object(adjustmentHead);
export const AdjustmentViewSchema = z.object({ ...adjustmentHead, note: z.string().nullable(), lines: z.array(AdjustmentLineViewSchema) });
export type AdjustmentView = z.infer<typeof AdjustmentViewSchema>;
export type AdjustmentSummary = z.infer<typeof AdjustmentSummarySchema>;
export const AdjustmentPageSchema = z.object({ items: z.array(AdjustmentSummarySchema), nextCursor: z.string().optional() });
export const AdjustmentListQuerySchema = z.object({ branchId: branchQuery, limit: limitQuery, cursor: cursorQuery });
export type AdjustmentListQuery = z.infer<typeof AdjustmentListQuerySchema>;

// --- Stock takes (UC-INVENTORY-007..010) -----------------------------------------------------------------------------------

/** BR-INVENTORY-016: a cycle count's scope — categories, brands, suppliers; together they narrow. */
export const StockTakeScopeSchema = z.strictObject({
  categories: z.unknown().superRefine((v, ctx) => {
    if (!Array.isArray(v) || v.length === 0 || v.some((c) => typeof c !== "string" || !(PRODUCT_CATEGORIES as readonly string[]).includes(c))) failField(ctx, "invalid", `categories among ${PRODUCT_CATEGORIES.join(", ")}`);
  }).transform((v) => [...new Set(v as ProductCategory[])]).optional(),
  brands: z.array(z.string().trim().min(1).max(60)).min(1).max(50).optional(),
  supplierIds: z.array(z.string().trim().min(1).max(64)).min(1).max(50).optional(),
});
export type StockTakeScope = z.infer<typeof StockTakeScopeSchema>;

export const StockTakeOpenInputSchema = z.strictObject({
  kind: enumField(STOCK_TAKE_KINDS, false).transform((v) => (isAbsent(v) ? null : (v as StockTakeKind))).optional(),
  scope: StockTakeScopeSchema.optional(),
  name: optionalString(INVENTORY_QUERY.stockTakeNameMax),
  note: optionalString(INVENTORY_LIMITS.noteMax),
  /** An opening count's cut-over date when known at the start; the posting sets it (UC-INVENTORY-010). */
  cutOverDate: dayField(false).transform((v) => (isAbsent(v) ? null : (v as string))).optional(),
});
export type StockTakeOpenInput = z.infer<typeof StockTakeOpenInputSchema>;

export const StockTakeLineViewSchema = z.object({
  /** The stored line's id; null for a found unit or serial derived from the entries of a count still open. */
  id: z.string().nullable(),
  /** The SKU and name frozen with the expected list, or with the line's first entry (BR-INVENTORY-006); the category as it is now. */
  productId: z.string(), sku: z.string(), productName: z.string(), category: z.enum(PRODUCT_CATEGORIES),
  serialId: z.string().nullable(), serialNumber: z.string().nullable(),
  /** InStore lines are counted; AtLab and InTransit lines are listed apart, confirmed, never counted (BR-INVENTORY-016). */
  expectedState: z.enum(STOCK_TAKE_EXPECTED_STATES),
  toConfirm: z.boolean(),
  /** The frozen figure plus the physical change the ledger recorded since the count opened (BR-INVENTORY-018). */
  expectedQty: z.number().int(),
  frozenQty: z.number().int(),
  movedDuringCountQty: z.number().int(),
  movedDuringCount: z.array(z.object({ type: z.enum(STOCK_MOVEMENT_TYPES), quantity: z.number().int(), document: StockDocumentRefSchema.nullable() })),
  countedQty: z.number().int(),
  varianceQty: z.number().int(),
  result: z.enum(STOCK_TAKE_LINE_RESULTS).nullable(),
  unitCostCents: z.number().int().nullable(),
  varianceValueCents: z.number().int(),
  /** The lab order or transfer holding a unit listed apart. */
  holdingDocument: StockDocumentRefSchema.nullable(),
  /** A unit listed apart that was scanned in the shop anyway (UC-INVENTORY-008 A5). */
  scannedHere: z.boolean(),
  /** A found serial: its status now and the branch it belongs to when another's (UC-INVENTORY-008 A3, A4). */
  serialStatus: z.enum(SERIAL_STATUSES).nullable(),
  foundInBranchId: z.string().nullable(), foundInBranchName: z.string().nullable(),
  /** Whether finalising posts anything for the line. */
  posts: z.boolean(),
  /**
   * An opening count whose posting in parts was ended (UC-INVENTORY-010 A8): a counted line no part posted, listed to be brought
   * in by an adjustment — it posts nothing and counts in none of the count's totals. False on every other line.
   */
  notPosted: z.boolean(),
  /**
   * A line `notPosted` (UC-INVENTORY-010 A8): the shelf price it was asked to keep (A6), which no part wrote — set again by hand: a
   * frame's own price on its serial once it is brought in, the branch's price under its Control PIN, or, for a product with no price
   * at all, its price on Products (A8 step 3); null when it carried none, and on every other line (the third review of DVN's answers,
   * 25 Oct 2026; the third way named since the verification of round 3, 25 Oct 2026).
   */
  shelfPriceCents: z.number().int().nullable(),
  /**
   * Which of those ways is the line's own, by what its part would have written (BR-PRICING-016), judged by the product as it is when
   * the count is read: `Product` — it has no price, so the shelf price would have been its price; `Serial` — a serialised product,
   * each frame's own price; `Branch` — the branch's price. Null where `shelfPriceCents` is (the verification of round 3, R3S-4).
   */
  shelfPriceOn: z.enum(SHELF_PRICE_TARGETS).nullable(),
  note: z.string().nullable(),
});
export type StockTakeLineView = z.infer<typeof StockTakeLineViewSchema>;

const stockTakeHead = {
  id: z.string(), number: z.string(), branchId: z.string(), branchName: z.string(),
  kind: z.enum(STOCK_TAKE_KINDS), status: z.enum(STOCK_TAKE_STATUSES), statusLabel: z.string(),
  scope: z.object({ categories: z.array(z.string()).optional(), brands: z.array(z.string()).optional(), supplierIds: z.array(z.string()).optional() }).nullable(),
  name: z.string().nullable(), note: z.string().nullable(),
  snapshotAt: z.string(), startedAt: z.string(), startedBy: InventoryUserRefSchema.nullable(),
  cutOverDate: z.string().nullable(),
  finalisedAt: z.string().nullable(), finalisedBy: InventoryUserRefSchema.nullable(), finaliseReason: z.string().nullable(),
  controlLogId: z.string().nullable(), authorisedBy: InventoryUserRefSchema.nullable(),
  abandonedAt: z.string().nullable(), abandonedBy: InventoryUserRefSchema.nullable(), abandonReason: z.string().nullable(),
  lineCount: z.number().int(), expectedCount: z.number().int(), countedCount: z.number().int(), varianceLines: z.number().int(),
  /**
   * The signed units the variance posts — or posted, once finalised — as the review's total reads (UC-INVENTORY-009 step 7). Not
   * counted less expected: a serial of another branch or a written-off one is counted and posts nothing (A10; TC-INVENTORY-161).
   */
  varianceQty: z.number().int(),
  varianceValueCents: z.number().int(), baseValuationCents: z.number().int().nullable(),
  /**
   * An opening stock posted in parts (UC-INVENTORY-010 A4): its lines posted and its lines to post — "Posting… 340 of 1 000" —
   * while it is Posting and once it is Finalised; null for a count posted in one write. `running` says whether its job runs,
   * read from the platform's record of the job each time the count is read: true only while the platform has it queued, running,
   * waiting or finishing a part before it pauses; false once the job has stopped — its retries used up, ended, paused, or never
   * started (E7) — and once the count is Finalised. `stoppedReason` says in plain words why a Posting count's job stopped — a
   * part's refusal by its sentence, any other failure "a part could not be written", never the platform's or the database's own
   * words — and is null while it runs and once Finalised. A Finalised count whose `done` is below its `total` had its posting
   * ended (A8): its lines not posted are marked `notPosted`, and `ended` names the end's Control Log row and who authorised it —
   * the count's `controlLogId` and `authorisedBy` stay the posting's; `ended` is null for any other count.
   */
  posting: z.object({
    done: z.number().int(), total: z.number().int(), running: z.boolean(), stoppedReason: z.string().nullable(),
    ended: z.object({ controlLogId: z.string(), authorisedBy: InventoryUserRefSchema.nullable() }).nullable(),
  }).nullable(),
};
export const StockTakeSummarySchema = z.object(stockTakeHead);
export type StockTakeSummary = z.infer<typeof StockTakeSummarySchema>;
export const StockTakeViewSchema = z.object({ ...stockTakeHead, lines: z.array(StockTakeLineViewSchema) });
export type StockTakeView = z.infer<typeof StockTakeViewSchema>;
export const StockTakePageSchema = z.object({ items: z.array(StockTakeSummarySchema), nextCursor: z.string().optional() });
export const StockTakeListQuerySchema = z.object({ branchId: branchQuery, status: enumQuery(STOCK_TAKE_STATUSES), limit: limitQuery, cursor: cursorQuery });
export type StockTakeListQuery = z.infer<typeof StockTakeListQuerySchema>;

/**
 * BR-INVENTORY-017: a count entry — a scanned code (one unit) or a product and a typed quantity (1–9 999); a found unit of a
 * serialised product names its product, and may keep the supplier's serial scanned for it and, at an opening count, an old tag
 * (UC-INVENTORY-010 A2, A3).
 */
export const StockCountInputSchema = z.strictObject({
  code: z.unknown().superRefine((v, ctx) => {
    if (typeof v !== "string" || v.trim() === "") return failField(ctx, "required", "a code");
    if (v.trim().length > 64) failField(ctx, "too_long", "at most 64 characters");
  }).transform((v) => (v as string).trim()).optional(),
  productId: optionalIdField(),
  quantity: optionalWhole({ min: 1, max: LINE_QUANTITY_MAX }),
  found: z.boolean().optional(),
  supplierSerial: z.unknown().superRefine((v, ctx) => {
    if (typeof v !== "string" || v.trim() === "") return failField(ctx, "required", "the supplier's serial");
    if (v.trim().length > SUPPLIER_SERIAL_MAX_LENGTH) return failField(ctx, "too_long", `at most ${SUPPLIER_SERIAL_MAX_LENGTH} characters`);
    if (/^\d{4}-\d{2}-\d{2}-\d{3,}$/.test(v.trim())) failField(ctx, "reserved_format", "has the shape of a serial number, which only Vision X issues");
  }).transform((v) => (v as string).trim()).optional(),
  tag: optionalString(INVENTORY_LIMITS.serialTagMax),
  device: optionalString(INVENTORY_QUERY.deviceMax),
}).superRefine((v, ctx) => {
  if (v.code === undefined && (v.productId === undefined || v.productId === null)) failField(ctx, "required", "scan a code or choose the product", ["code"]);
});
export type StockCountInput = z.infer<typeof StockCountInputSchema>;

export const StockCountEntryViewSchema = z.object({
  /** The SKU and name frozen when the entry was made (BR-INVENTORY-006). */
  id: z.string(), stockTakeId: z.string(), productId: z.string(), sku: z.string(), productName: z.string(),
  serialId: z.string().nullable(), serialNumber: z.string().nullable(), quantity: z.number().int(), found: z.boolean(),
  code: z.string().nullable(), supplierSerial: z.string().nullable(), tag: z.string().nullable(), device: z.string().nullable(),
  countedBy: InventoryUserRefSchema.nullable(), countedAt: z.string(),
  voidedAt: z.string().nullable(), voidedBy: InventoryUserRefSchema.nullable(),
});
export type StockCountEntryView = z.infer<typeof StockCountEntryViewSchema>;
/** A count entry's answer: the entry and its line now — or, for a serial already counted, who counted it (UC-INVENTORY-008 A2). */
export const StockCountResultSchema = z.object({
  entry: StockCountEntryViewSchema.nullable(),
  line: StockTakeLineViewSchema.nullable(),
  alreadyCounted: z.object({ entryId: z.string(), by: z.string(), byUser: InventoryUserRefSchema.nullable(), device: z.string().nullable(), at: z.string() }).nullable(),
  matchedOn: z.enum(COUNT_MATCHES),
  message: z.string(),
});
export type StockCountResult = z.infer<typeof StockCountResultSchema>;
export const StockCountListQuerySchema = z.object({ productId: idQuery, limit: limitQuery, cursor: cursorQuery });
export const StockCountPageSchema = z.object({ items: z.array(StockCountEntryViewSchema), nextCursor: z.string().optional() });
export const StockCountParamsSchema = z.object({ id: z.string().min(1).max(64), countId: z.string().min(1).max(64) });

export const VarianceQuerySchema = z.object({
  /** An opening count's cut-over date for the costs pricing answers (UC-INVENTORY-010 step 4); the count's own by default. */
  cutOverDate: dayQuery,
});
/** UC-INVENTORY-009 step 2: each line's expected, counted, variance and value, the lines apart, and the totals. */
export const VarianceViewSchema = z.object({
  stockTakeId: z.string(), number: z.string(), kind: z.enum(STOCK_TAKE_KINDS), status: z.enum(STOCK_TAKE_STATUSES),
  lines: z.number().int(), varianceLines: z.number().int(),
  expected: z.number().int(), counted: z.number().int(), varianceQty: z.number().int(), varianceValueCents: z.number().int(),
  items: z.array(StockTakeLineViewSchema),
  /**
   * An opening count: each counted product's cost where none is typed — its lab's price list's (`PriceList`, with the list), else its
   * supplier price net of its supplier's default discount (`SupplierPrice`, no list; DVN's answers of 28 Oct 2026, change 9), else none
   * (UC-INVENTORY-010 A1).
   */
  costs: z.array(z.object({
    productId: z.string(), sku: z.string(), countedQty: z.number().int(), unitCostCents: z.number().int().nullable(),
    source: z.enum(["PriceList", "SupplierPrice", "None"]), priceListId: z.string().nullable(), priceListName: z.string().nullable(), valueCents: z.number().int().nullable(),
  })),
});
export type VarianceView = z.infer<typeof VarianceViewSchema>;

/** BR-INVENTORY-019: a reason when something differs (≤ 500) and the variance the user reviewed, in cents (UC-INVENTORY-009 E8). */
export const FinaliseInputSchema = z.strictObject({
  reason: optionalString(INVENTORY_LIMITS.noteMax),
  reviewedVarianceCents: z.unknown().superRefine((v, ctx) => {
    if (!isAbsent(v) && (typeof v !== "number" || !Number.isSafeInteger(v))) failField(ctx, "invalid", "whole cents");
  }).transform((v) => (isAbsent(v) ? null : (v as number))).optional(),
  /** The variance's signed units as reviewed — a change of units at an unchanged value refuses too (UC-INVENTORY-009 E8). */
  reviewedVarianceQty: z.unknown().superRefine((v, ctx) => {
    if (!isAbsent(v) && (typeof v !== "number" || !Number.isSafeInteger(v))) failField(ctx, "invalid", "whole units");
  }).transform((v) => (isAbsent(v) ? null : (v as number))).optional(),
});
export type FinaliseInput = z.infer<typeof FinaliseInputSchema>;
export const AbandonInputSchema = z.strictObject({ reason: requiredString(INVENTORY_LIMITS.noteMax) });
export type AbandonInput = z.infer<typeof AbandonInputSchema>;
/** UC-INVENTORY-010 A8 (Proposed): ending an opening count's posting in parts whose job has stopped — why, at most 500 characters. */
export const EndPostingInputSchema = z.strictObject({ reason: requiredString(INVENTORY_LIMITS.noteMax) });
export type EndPostingInput = z.infer<typeof EndPostingInputSchema>;

/** "20 000": a count with the design system's plain-space thousands, for a contract message. */
const thousands = (n: number): string => String(n).replace(/\B(?=(\d{3})+(?!\d))/g, " ");
const OPENING_LINES = thousands(INVENTORY_LIMITS.openingLinesMax);

/**
 * UC-INVENTORY-010 step 5: the count, the cut-over date, each line's typed cost and any shelf prices (A6) — each at most as many as
 * the lines one opening posts (E14, BR-INVENTORY-027; 5 000 each until the third review of DVN's answers, 25 Oct 2026, no rule saying so).
 */
export const OpeningBalanceInputSchema = z.strictObject({
  stockTakeId: requiredIdField(),
  cutOverDate: dayField(true).transform((v) => v as string),
  costs: z.array(z.strictObject({ productId: requiredIdField(), unitCostCents: requiredWhole({ min: 0, max: UNIT_COST_MAX_CENTS }) }))
    .max(INVENTORY_LIMITS.openingLinesMax, `At most ${OPENING_LINES} typed costs: one opening posts at most ${OPENING_LINES} lines`).optional(),
  prices: z.array(z.strictObject({ productId: requiredIdField(), priceCents: requiredWhole({ min: 0, max: UNIT_COST_MAX_CENTS }) }))
    .max(INVENTORY_LIMITS.openingLinesMax, `At most ${OPENING_LINES} shelf prices: one opening posts at most ${OPENING_LINES} lines`).optional(),
});
export type OpeningBalanceInput = z.infer<typeof OpeningBalanceInputSchema>;

// --- Transfers (UC-INVENTORY-011, -012) ------------------------------------------------------------------------------------

export const TransferLineInputSchema = z.strictObject({
  productId: optionalIdField(),
  serialId: optionalIdField(),
  quantity: optionalWhole({ min: 1, max: LINE_QUANTITY_MAX }),
}).superRefine((v, ctx) => {
  if (!v.productId && !v.serialId) failField(ctx, "required", "a serial, or a product and a quantity", ["productId"]);
  if (v.serialId && v.quantity !== undefined && v.quantity !== null && v.quantity !== 1) failField(ctx, "serial_unit", "a serial is one unit", ["quantity"]);
});
export type TransferLineInput = z.infer<typeof TransferLineInputSchema>;

const transferLines = z.unknown().superRefine((v, ctx) => {
  if (!Array.isArray(v)) return failField(ctx, "invalid", "a list of lines");
  if (v.length > INVENTORY_LIMITS.documentLinesMax) failField(ctx, "too_many", `at most ${INVENTORY_LIMITS.documentLinesMax} lines`);
}).pipe(z.array(TransferLineInputSchema)).superRefine((lines, ctx) => {
  const seen = new Set<string>();
  lines.forEach((l, i) => {
    if (!l.serialId) return;
    if (seen.has(l.serialId)) failField(ctx, "duplicate", "the same serial twice", [i, "serialId"]);
    seen.add(l.serialId);
  });
});

/** BR-INVENTORY-021: a draft to another Active branch with a reason (1–500) and up to 200 lines. */
export const TransferInputSchema = z.strictObject({
  toBranchId: requiredIdField(),
  reason: requiredString(INVENTORY_LIMITS.noteMax),
  lines: transferLines.optional(),
});
export type TransferInput = z.infer<typeof TransferInputSchema>;
/** UC-INVENTORY-011 A2: a draft's edit — what changes; `lines` replaces the lines. */
export const TransferPatchSchema = z.strictObject({
  toBranchId: optionalIdField(),
  reason: z.unknown().superRefine((v, ctx) => checkLength(v, INVENTORY_LIMITS.noteMax, ctx, true)).transform((v) => (v as string).trim()).optional(),
  lines: transferLines.optional(),
});
export type TransferPatch = z.infer<typeof TransferPatchSchema>;
export const TransferCancelInputSchema = z.strictObject({ reason: requiredString(INVENTORY_LIMITS.noteMax) });
export type TransferCancelInput = z.infer<typeof TransferCancelInputSchema>;
export const TransferReceiveInputSchema = z.strictObject({
  lines: z.array(z.strictObject({
    lineId: requiredIdField(),
    serialIds: z.array(z.string().trim().min(1).max(64)).max(LINE_QUANTITY_MAX).optional(),
    quantity: optionalWhole({ min: 1, max: LINE_QUANTITY_MAX }),
  })).min(1).max(INVENTORY_LIMITS.documentLinesMax),
});
export type TransferReceiveInput = z.infer<typeof TransferReceiveInputSchema>;

export const TransferLineViewSchema = z.object({
  /** The SKU and name frozen when the line was added to the draft (BR-INVENTORY-006); the supplier's name as it is now. */
  id: z.string(), productId: z.string(), sku: z.string(), productName: z.string(), supplierName: z.string().nullable(),
  /** The product's barcodes (a box's EAN), so a quantity line is received by scanning its packs (UC-INVENTORY-012 A3). */
  barcodes: z.array(z.string()),
  serialId: z.string().nullable(), serialNumber: z.string().nullable(),
  /** The serial's status now (shown In store while Available), or null for a quantity line. */
  serialStatus: z.enum(SERIAL_STATUSES).nullable(),
  quantity: z.number().int(), quantityReceived: z.number().int(), quantityReturned: z.number().int(), quantityInTransit: z.number().int(),
  /** The sender's weighted average now while a draft; the cost snapshotted at Send afterwards (BR-INVENTORY-006, -021). */
  unitCostCents: z.number().int(), valueCents: z.number().int(),
  costSnapshotted: z.boolean(),
  /** The sender's available for the product now (UC-INVENTORY-011 step 4). */
  available: z.number().int(),
  outMovementId: z.string().nullable(), inMovementId: z.string().nullable(),
});
export type TransferLineView = z.infer<typeof TransferLineViewSchema>;

const branchRef = z.object({ id: z.string(), code: z.string(), name: z.string() });
const transferHead = {
  id: z.string(), number: z.string(), status: z.enum(STOCK_TRANSFER_STATUSES), statusLabel: z.string(),
  fromBranch: branchRef, toBranch: branchRef,
  reason: z.string(),
  createdAt: z.string(), createdBy: InventoryUserRefSchema.nullable(),
  sentAt: z.string().nullable(), sentBy: InventoryUserRefSchema.nullable(),
  receivedAt: z.string().nullable(), receivedBy: InventoryUserRefSchema.nullable(),
  cancelledAt: z.string().nullable(), cancelledBy: InventoryUserRefSchema.nullable(), cancelReason: z.string().nullable(),
  units: z.number().int(), unitsReceived: z.number().int(), unitsReturned: z.number().int(),
  totalValueCents: z.number().int(),
  version: z.number().int(),
};
export const TransferSummarySchema = z.object(transferHead);
export type TransferSummary = z.infer<typeof TransferSummarySchema>;
export const TransferViewSchema = z.object({ ...transferHead, lines: z.array(TransferLineViewSchema) });
export type TransferView = z.infer<typeof TransferViewSchema>;
export const TransferPageSchema = z.object({ items: z.array(TransferSummarySchema), nextCursor: z.string().optional() });
export const TransferListQuerySchema = z.object({
  direction: enumQuery(STOCK_TRANSFER_DIRECTIONS),
  branchId: branchQuery,
  status: enumQuery(STOCK_TRANSFER_STATUSES),
  limit: limitQuery,
  cursor: cursorQuery,
});
export type TransferListQuery = z.infer<typeof TransferListQuerySchema>;

